// Component: StrategyLineage.jsx
// Purpose: Strategy-version lineage view — compare versions and restore a
//          version as primary. Surfaces the R45 deploy-gate allocation
//          rejection (code + message) via the snackbar on activate failure.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Icon, Segmented, Checkbox, Tooltip } from '../../../components/ds';
import StrategyCompare from './StrategyCompare';
import { mockLineageData, mockStrategyProjects } from './mockData';
import StrategyService from '../../../api/services/strategies';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { extractAllocationError } from '../../../utils/apiErrorUtils';

const toDateLabel = (timestamp) => {
  if (!timestamp) return 'Unknown';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const normalizeVersion = (version, index, currentVersionId) => {
  const id = String(version.id || version.version_id || `version-${index + 1}`);
  const parentIdRaw = version.parent_id || version.parent_version_id || null;
  const parentId = parentIdRaw ? String(parentIdRaw) : null;

  const defaultStatus = id === String(currentVersionId || '') ? 'Active' : 'Tested';
  return {
    id,
    parent_id: parentId,
    name: version.name || `Version ${version.version_number || index + 1}`,
    message: version.message || version.change_description || 'No description provided',
    timestamp: version.timestamp || version.created_at || new Date().toISOString(),
    status: version.status || defaultStatus,
    tag: version.tag || version.version_number || `v${index + 1}.0`,
    metrics: {
      winRate: Number(version.metrics?.winRate || version.metrics?.win_rate || 0),
      profitFactor: Number(version.metrics?.profitFactor || version.metrics?.profit_factor || 0),
    },
  };
};

const buildMockParents = (versions) =>
  versions.map((version, index) => ({
    ...version,
    parent_id: version.parent_id || (index < versions.length - 1 ? versions[index + 1].id : null),
  }));

// --> Status → DS token color + badge variant + icon (replaces MUI theme refs)
const statusStyle = (version) => {
  const isActive = version.status === 'Active';
  const isFailed = version.status === 'Failed';
  const isExp = version.tag === 'exp';
  if (isActive) return { color: 'var(--color-profit)', variant: 'profit', icon: <Icon name="success" size={13} /> };
  if (isFailed) return { color: 'var(--color-loss)', variant: 'loss', icon: <Icon name="bug" size={13} /> };
  if (isExp) return { color: 'var(--warning)', variant: 'warn', icon: <Icon name="flask" size={13} /> };
  return { color: 'var(--badge-info-dot)', variant: 'info', icon: <Icon name="success" size={13} /> };
};

const VersionDetails = ({ version, onEditBacktest, onRestorePrimary, isRestoring }) => {
  if (!version) {
    return (
      <div className="h-full flex items-center justify-center text-center rounded-2xl border border-[var(--divider)] bg-bg-1 p-8">
        <p className="text-sm text-fg-3">Select a version from the timeline to view details</p>
      </div>
    );
  }

  const isActive = version.status === 'Active';
  const { color: statusColor, variant: statusVariant, icon } = statusStyle(version);
  const statusGlow = `color-mix(in oklch, ${statusColor} 15%, transparent)`;

  return (
    <div className="relative h-full overflow-y-auto rounded-2xl border border-[var(--divider)] bg-bg-2">
      {isActive && (
        <div
          className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none"
          style={{ background: `linear-gradient(180deg, ${statusGlow} 0%, transparent 100%)` }}
        />
      )}
      <div className="relative z-[1] p-6 flex flex-col">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-extrabold text-fg-1 mb-0.5 tracking-tight">{version.name}</h2>
            <p className="text-xs text-fg-3 font-medium tracking-wide">{toDateLabel(version.timestamp)}</p>
          </div>
          <span className={`ds-badge sm ${statusVariant}`}>
            {icon}
            {version.status}
          </span>
        </div>

        <hr className="border-0 border-t border-[var(--divider)] my-4 opacity-60" />

        <div className="mb-4">
          <p className="ds-eyebrow text-fg-3 mb-1">Commit Message</p>
          <p className="text-sm leading-relaxed text-fg-2 bg-bg-1 p-3 rounded-lg border border-[var(--divider)]">
            {version.message}
          </p>
        </div>

        <p className="ds-eyebrow text-fg-3 mb-1">Performance Metrics</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-4 rounded-xl border border-[var(--divider)] bg-bg-1 flex flex-col justify-center">
            <p className="ds-eyebrow text-fg-3 mb-1">Win Rate</p>
            <p className={`text-2xl font-extrabold tracking-tight ${version.metrics.winRate > 50 ? 'text-profit' : 'text-loss'}`}>
              {version.metrics.winRate}%
            </p>
          </div>
          <div className="p-4 rounded-xl border border-[var(--divider)] bg-bg-1 flex flex-col justify-center">
            <p className="ds-eyebrow text-fg-3 mb-1">Profit Factor</p>
            <p className="text-2xl font-extrabold tracking-tight text-fg-1">{version.metrics.profitFactor}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-3">
          <button
            type="button"
            className="btn btn-primary btn-lg w-full"
            onClick={() => onEditBacktest(version.id)}
          >
            <Icon name="play" size={16} />
            Edit / Backtest
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-lg w-full"
            disabled={isActive || isRestoring}
            onClick={() => onRestorePrimary(version.id)}
          >
            <Icon name="restore" size={16} />
            {isRestoring ? 'Restoring...' : (isActive ? 'Current Primary' : 'Restore as Primary')}
          </button>
        </div>
      </div>
    </div>
  );
};

const StrategyLineage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [project, setProject] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [compareList, setCompareList] = useState([]);
  const [isCompareOpen, setIsCompareOpen] = useState(false);
  const [lineageError, setLineageError] = useState('');
  const [filter, setFilter] = useState('All');
  const [isRestoring, setIsRestoring] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const { showSnackbar } = useSnackbar();

  const handleCompareToggle = useCallback((id) => {
    setCompareList((prev) => {
      if (prev.includes(id)) {
        return prev.filter((compareId) => compareId !== id);
      }
      if (prev.length >= 3) {
        return prev;
      }
      return [...prev, id];
    });
  }, []);

  const handleEditBacktest = useCallback(
    (versionId) => {
      // Navigate to the main edit studio with this strategy and specific version
      navigate(`/strategies/edit/${projectId}/version/${versionId}`);
    },
    [navigate, projectId]
  );

  const handleRestorePrimary = useCallback(async (versionId) => {
    setIsRestoring(true);
    try {
      await StrategyService.activateVersion(projectId, versionId);
      showSnackbar('Strategy version restored as primary!', 'success');
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('[DIAG] activateVersion restore failed', err);
      const allocErr = extractAllocationError(err);
      if (allocErr) {
        //--> R45 deploy-gate rejection: show the actionable backend message + code
        showSnackbar(`Cannot activate version: ${allocErr.message} (${allocErr.code})`, 'error');
      } else {
        //--> Non-allocation error: unchanged legacy generic fallback (no regression)
        showSnackbar('Failed to restore version: ' + (err.message || 'Unknown error'), 'error');
      }
    } finally {
      setIsRestoring(false);
    }
  }, [projectId, showSnackbar]);

  useEffect(() => {
    let isCurrent = true;

    const fetchData = async () => {
      // If we already have versions for this project, this is a silent refresh rather than a full page load
      const isBackgroundRefresh = versions.length > 0 && project?.id === projectId;

      if (!isBackgroundRefresh) {
        setLoading(true);
        setSelectedVersion(null);
        setCompareList([]);
        setIsCompareOpen(false);
      }
      setLineageError('');

      try {
        const loadMock = () => {
          const mockProject = mockStrategyProjects.find((item) => item.id === projectId) || null;
          const mockVersions = buildMockParents(mockLineageData[projectId] || []);
          const normalized = mockVersions.map((item, index) => normalizeVersion(item, index, null));
          if (!isCurrent) return;
          setProject(mockProject);
          setVersions(normalized);
          if (!isBackgroundRefresh) {
            setSelectedVersion(normalized.find(v => v.status === 'Active') || normalized[0] || null);
          } else {
            setSelectedVersion(prev => prev ? (normalized.find(v => v.id === prev.id) || normalized.find(v => v.status === 'Active') || normalized[0] || null) : (normalized.find(v => v.status === 'Active') || normalized[0] || null));
          }
        };

        try {
          const [strategyResult, versionsResult] = await Promise.allSettled([
            StrategyService.getStrategyById(projectId),
            StrategyService.getStrategyVersions(projectId)
          ]);

          if (strategyResult.status === 'rejected') {
            throw strategyResult.reason;
          }

          const strategyRes = strategyResult.value;
          const strategyData = strategyRes.data;

          if (!isCurrent) return;

          setProject({
            id: strategyData.strategy_id,
            name: strategyData.name,
            description: strategyData.description,
            status: strategyData.status,
          });

          if (versionsResult.status === 'fulfilled') {
            const versionsRes = versionsResult.value;
            const list = versionsRes?.data?.versions || [];

            if (Array.isArray(list) && list.length > 0) {
              const normalized = list.map((item, index) =>
                normalizeVersion(item, index, strategyData.current_version_id)
              );
              // Do NOT force linear parent_id fallback. The backend or semantics should govern branching.
              // If we absolutely must infer from tag names, we could, but let's trust the backend or default to null.
              setVersions(normalized);
              if (!isBackgroundRefresh) {
                setSelectedVersion(normalized.find(v => v.status === 'Active') || normalized[0] || null);
              } else {
                setSelectedVersion(prev => prev ? (normalized.find(v => v.id === prev.id) || normalized.find(v => v.status === 'Active') || normalized[0] || null) : (normalized.find(v => v.status === 'Active') || normalized[0] || null));
              }
            } else {
              const singleVersion = normalizeVersion(
                {
                  id: strategyData.current_version_id || 'v1',
                  version_number: strategyData.version_number || '1.0',
                  change_description: 'Initial version',
                  created_at: strategyData.updated_at || strategyData.created_at,
                  parent_version_id: null,
                  metrics: { winRate: 0, profitFactor: 0 },
                  status: 'Active',
                },
                0,
                strategyData.current_version_id
              );
              setVersions([singleVersion]);
              if (!isBackgroundRefresh) {
                setSelectedVersion(singleVersion);
              } else {
                // Background refresh: always update to the single current version
                setSelectedVersion(singleVersion);
              }
              setLineageError('Version history is empty; showing current version only.');
            }
          } else {
            console.error('Failed to fetch versions', versionsResult.reason);
            const singleVersion = normalizeVersion(
              {
                id: strategyData.current_version_id || 'v1',
                version_number: strategyData.version_number || '1.0',
                change_description: 'Initial version',
                created_at: strategyData.updated_at || strategyData.created_at,
                parent_version_id: null,
                metrics: { winRate: 0, profitFactor: 0 },
                status: 'Active',
              },
              0,
              strategyData.current_version_id
            );
            setVersions([singleVersion]);
            if (!isBackgroundRefresh) {
              setSelectedVersion(singleVersion);
            } else {
              // Background refresh: always update to the single current version
              setSelectedVersion(singleVersion);
            }
            setLineageError('Unable to fetch versions; showing current version only.');
          }

        } catch (strategyError) {
          const status = strategyError?.response?.status;
          if (status === 404) {
            loadMock();
          } else {
            throw strategyError;
          }
        }
      } catch (error) {
        console.error('Failed to fetch strategy details', error);
        if (!isCurrent) return;
        setProject(null);
        setVersions([]);
        setLineageError('Unable to load strategy lineage at this time.');
      } finally {
        if (isCurrent) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCurrent = false;
    };
  }, [projectId, refreshKey]);

  const layoutData = useMemo(() => {
    // Guard: return empty layout if no versions exist to prevent crash on versions[0] access
    if (versions.length === 0) return [];

    const layout = [];
    const colors = ['#0ea5e9', '#d946ef', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

    // Find highest semantic version for main trunk priority
    const parseVersion = (name) => {
      if (!name) return [0,0,0];
      const match = name.match(/v(\d+)\.(\d+)(?:\.(\d+))?/i);
      if (!match) return [0, 0, 0];
      return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3] || 0)];
    };

    let maxVer = versions[0];
    versions.forEach(v => {
      const vMax = parseVersion(maxVer.name);
      const vCur = parseVersion(v.name);
      if (vCur[0] !== vMax[0]) { if (vCur[0] > vMax[0]) maxVer = v; }
      else if (vCur[1] !== vMax[1]) { if (vCur[1] > vMax[1]) maxVer = v; }
      else if (vCur[2] > vMax[2]) { maxVer = v; }
    });

    const mainTrunkIds = new Set();
    let currentMain = maxVer;
    while (currentMain) {
      mainTrunkIds.add(currentMain.id);
      if (!currentMain.parent_id) break;
      currentMain = versions.find(v => v.id === currentMain.parent_id);
    }

    let activeBranches = [];

    versions.forEach((v) => {
      const incomingLines = activeBranches.map(b => ({ ...b }));
      const branchesToMe = activeBranches.filter(b => b.targetId === v.id);
      const usedLanes = new Set(activeBranches.map(b => b.lane));

      let cLane = 0;
      let cColor = colors[0];

      if (branchesToMe.length > 0) {
        if (mainTrunkIds.has(v.id)) {
           cLane = 0;
        } else {
           branchesToMe.sort((a,b) => a.lane - b.lane);
           cLane = branchesToMe[0].lane;
        }
        cColor = colors[cLane % colors.length];
      } else {
        if (!mainTrunkIds.has(v.id)) {
           while (usedLanes.has(cLane) || cLane === 0) cLane++;
        }
        cColor = colors[cLane % colors.length];
      }

      // Remove lines merging into this node
      activeBranches = activeBranches.filter(b => b.targetId !== v.id);

      const incomingMerges = branchesToMe
        .filter(b => b.lane !== cLane)
        .map(b => ({ fromLane: b.lane, color: b.colorIndex || colors[b.lane % colors.length] }));

      const straightIncoming = branchesToMe.some(b => b.lane === cLane);

      let spawnedBranch = null;
      if (v.parent_id) {
         let pLane = cLane; // branch stays on its lane until parent

         const existing = activeBranches.find(b => b.targetId === v.parent_id && b.lane === pLane);
         if (!existing) {
             const pColor = colors[pLane % colors.length];
             activeBranches.push({ targetId: v.parent_id, lane: pLane, colorIndex: pColor });
             spawnedBranch = { lane: pLane, colorIndex: pColor };
         }
      }

      const outgoingLines = activeBranches.map(b => ({ ...b }));

      layout.push({
        version: v,
        lane: cLane,
        color: cColor,
        incomingLines,
        incomingMerges,
        straightIncoming,
        spawnedBranch,
        outgoingLines
      });
    });

    return layout;
  }, [versions]);

  const maxLanes = useMemo(() => {
    let max = 0;
    layoutData.forEach(item => {
      max = Math.max(max, item.lane, ...item.incomingLines.map(b => b.lane), ...item.outgoingLines.map(b => b.lane));
    });
    return max;
  }, [layoutData]);

  const compareVersions = useMemo(
    () => versions.filter((version) => compareList.includes(version.id)),
    [versions, compareList]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[var(--divider)] border-t-[var(--theme-purple)] rounded-full animate-spin" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
        <div className="text-center py-24">
          <p className="text-lg font-semibold text-fg-2">Project Not Found</p>
          <button type="button" className="btn btn-ghost mt-3" onClick={() => navigate('/Strategies/Modern')}>
            <Icon name="back" size={16} />
            Back to Strategy Hub
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-main-bg dark:bg-main-dark-bg pt-0 px-4 md:px-6 pb-10">
      <div className="max-w-[1400px] mx-auto">
        {/* Header Section */}
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <button
              type="button"
              className="btn btn-ghost mb-1"
              onClick={() => navigate('/Strategies/Modern')}
            >
              <Icon name="back" size={16} />
              Back to Strategy Hub
            </button>
            <h1 className="text-2xl font-extrabold text-fg-1 flex items-center gap-2">
              {project.name}
              <span className="ds-badge sm info">Lineage</span>
            </h1>
          </div>

          {/* Quick Filters */}
          <Segmented
            aria-label="Version filter"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'All', label: 'All' },
              { value: 'Active', label: 'Active' },
              { value: 'Tested', label: 'Tested' },
              { value: 'Failed', label: 'Failed' },
            ]}
          />
        </div>

        {lineageError && (
          <div className="p-3 mb-4 rounded-lg text-sm font-medium bg-[var(--badge-warn-bg)] text-[var(--badge-warn-fg)]">
            {lineageError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Timeline List */}
          <div className="md:col-span-7 lg:col-span-8">
            <div className="sm:pl-2">
              {layoutData.map((item) => {
                const { version, lane, color, incomingLines, incomingMerges, straightIncoming, spawnedBranch } = item;
                const isSelected = selectedVersion?.id === version.id;
                const isCompareSelected = compareList.includes(version.id);
                const isActive = version.status === 'Active';
                const isFilteredOut = filter !== 'All' && version.status !== filter;

                const { color: statusColor, variant: statusVariant, icon } = statusStyle(version);
                const statusGlow = `color-mix(in oklch, ${statusColor} 15%, transparent)`;

                const getX = (l) => 20 + l * 28; // Tighter lanes
                const dot_y = 52; // Tighter vertical padding
                const graphWidth = 40 + maxLanes * 28;

                return (
                  <div
                    key={version.id}
                    className="flex flex-row relative transition-opacity duration-200"
                    style={{ opacity: isFilteredOut ? 0.35 : 1 }}
                  >
                    {/* Graph Column */}
                    <div className="flex-shrink-0 relative" style={{ width: graphWidth }}>
                      <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, overflow: 'visible' }}>
                        {/* Pass-through and incoming lines above dot */}
                        {incomingLines.map(il => {
                          const isMerging = incomingMerges.some(m => m.fromLane === il.lane);
                          const isTargetingUsStraight = straightIncoming && il.lane === lane;

                          if (isMerging) {
                             // Stop at dot_y - 20 to prepare for curve
                             return <line key={`in-${il.lane}`} x1={getX(il.lane)} y1={0} x2={getX(il.lane)} y2={dot_y - 20} stroke={il.colorIndex} strokeWidth={3} strokeLinecap="round" />;
                          } else if (isTargetingUsStraight) {
                             // Stop exactly at dot
                             return <line key={`in-${il.lane}`} x1={getX(il.lane)} y1={0} x2={getX(il.lane)} y2={dot_y} stroke={il.colorIndex} strokeWidth={3} strokeLinecap="round" />;
                          } else {
                             // Pass through completely
                             return <line key={`in-${il.lane}`} x1={getX(il.lane)} y1={0} x2={getX(il.lane)} y2="100%" stroke={il.colorIndex} strokeWidth={3} strokeLinecap="round" />;
                          }
                        })}

                        {/* Curves to dot */}
                        {incomingMerges.map(m => {
                           const x1 = getX(m.fromLane);
                           const x2 = getX(lane);
                           const y1 = dot_y - 20;
                           const y2 = dot_y;
                           return <path key={`merge-${m.fromLane}`} d={`M ${x1} ${y1} C ${x1} ${y1 + 10}, ${x2} ${y2 - 10}, ${x2} ${y2}`} fill="none" stroke={m.color} strokeWidth={3} strokeLinecap="round" />;
                        })}

                        {/* Spawned lines below dot */}
                        {spawnedBranch && (
                           <line key={`spawn-${spawnedBranch.lane}`} x1={getX(spawnedBranch.lane)} y1={dot_y} x2={getX(spawnedBranch.lane)} y2="100%" stroke={spawnedBranch.colorIndex} strokeWidth={3} strokeLinecap="round" />
                        )}

                        {/* Dot */}
                        <circle cx={getX(lane)} cy={dot_y} r={7} fill={color} stroke="var(--bg-2)" strokeWidth={3} />
                      </svg>
                    </div>

                    {/* Compact Card Data */}
                    <div className="flex-grow min-w-0 pb-4">
                      <div
                        className="relative overflow-hidden rounded-lg border cursor-pointer transition-all duration-200 hover:-translate-y-px hover:shadow-md"
                        style={{
                          borderColor: isSelected ? statusColor : 'var(--divider)',
                          background: isSelected ? 'var(--bg-1)' : 'var(--bg-2)',
                          ...(isSelected ? { boxShadow: `0 0 0 1px ${statusColor}, 0 4px 12px ${statusGlow}` } : {}),
                        }}
                        onClick={() => setSelectedVersion(version)}
                      >
                        {isActive && (
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{ background: `linear-gradient(90deg, ${statusGlow} 0%, transparent 100%)` }}
                          />
                        )}

                        <div className="relative z-[1] p-4">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`ds-badge xs ${statusVariant}`}>
                                {icon}
                                {version.tag}
                              </span>
                              <span className="text-sm font-bold text-fg-1 tracking-tight">
                                {version.name}
                              </span>
                            </div>

                            <span onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                              <Tooltip content="Compare">
                                <Checkbox
                                  checked={isCompareSelected}
                                  onChange={() => handleCompareToggle(version.id)}
                                  aria-label="Select version to compare"
                                />
                              </Tooltip>
                            </span>
                          </div>

                          <p className="text-sm text-fg-3 truncate mb-3">
                            {version.message}
                          </p>

                          <div className="flex justify-between items-center pt-3 border-t border-[var(--divider)]">
                            <span className="text-xs text-fg-3 font-medium">
                              {toDateLabel(version.timestamp)}
                            </span>

                            <div className="flex gap-3">
                              <span className="text-xs text-fg-3">WR: <span className="font-bold" style={{ color: version.metrics.winRate > 50 ? 'var(--color-profit)' : 'var(--color-loss)' }}>{version.metrics.winRate}%</span></span>
                              <span className="text-xs text-fg-3">PF: <span className="font-bold text-fg-1">{version.metrics.profitFactor}</span></span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {layoutData.length === 0 && (
                <p className="text-fg-3 py-16 text-center">
                  No strategy versions found.
                </p>
              )}
            </div>
          </div>

          {/* Details Panel */}
          <div className="md:col-span-5 lg:col-span-4">
            <div className="sticky top-[100px] h-[calc(100vh-120px)]">
              <VersionDetails
                version={selectedVersion}
                onEditBacktest={handleEditBacktest}
                onRestorePrimary={handleRestorePrimary}
                isRestoring={isRestoring}
              />
            </div>
          </div>
        </div>

        {/* Compare Floating Action Bar */}
        {compareVersions.length > 1 && (
          <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-4 px-5 py-3 rounded-full bg-bg-2 border border-[var(--divider)] shadow-2xl">
            <span className="text-sm font-bold text-fg-1">
              {compareVersions.length} Versions Selected
            </span>
            <button
              type="button"
              className="btn btn-primary px-3"
              onClick={() => setIsCompareOpen(true)}
            >
              <Icon name="compare" size={16} />
              Compare Side-by-Side
            </button>
          </div>
        )}

        <StrategyCompare isOpen={isCompareOpen} onClose={() => setIsCompareOpen(false)} versions={compareVersions} />
      </div>
    </div>
  );
};

export default StrategyLineage;
